import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { locationsRes, safe } from "../../lib/locations";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, Path, CheckCircle, MagnifyingGlass,
  UploadSimple, Download, FileCsv, CaretUp, CaretDown,
} from "@phosphor-icons/react";

export default function Zones() {
  const [zones, setZones] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    id: null, name: "", district_id: "", lsgd_id: "", ward_ids: [],
  });
  const [lsgdSearch, setLsgdSearch] = useState("");
  const [listFilter, setListFilter] = useState({ district_id: "", lsgd_id: "", q: "" });
  const [sort, setSort] = useState({ col: "name", dir: "asc" });
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const districts = useMemo(() => locations.filter((x) => x.type === "district"), [locations]);
  const lsgds = useMemo(() => locations.filter((x) => x.type === "lsgd"), [locations]);
  const wards = useMemo(() => locations.filter((x) => x.type === "ward"), [locations]);

  const load = async () => {
    try {
      const [z, l] = await Promise.all([
        safe(api.get("/zones")),
        locationsRes(),
      ]);
      setZones(z.data);
      setLocations(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const lsgdName = (id) => lsgds.find((l) => l.id === id)?.name;
  const districtName = (id) => districts.find((d) => d.id === id)?.name;

  const districtLsgds = useMemo(() => {
    let rows = form.district_id ? lsgds.filter((l) => l.parent_id === form.district_id) : lsgds;
    if (lsgdSearch) rows = rows.filter((l) => l.name.toLowerCase().includes(lsgdSearch.toLowerCase()));
    return rows;
  }, [lsgds, form.district_id, lsgdSearch]);

  const lsgdWards = useMemo(
    () => form.lsgd_id ? wards.filter((w) => w.parent_id === form.lsgd_id) : [],
    [wards, form.lsgd_id],
  );

  const takenWards = useMemo(() => {
    const s = new Set();
    zones.forEach((z) => { if (z.id !== form.id) (z.ward_ids || []).forEach((w) => s.add(w)); });
    return s;
  }, [zones, form.id]);

  const toggleWard = (id) => setForm((f) => ({
    ...f,
    ward_ids: f.ward_ids.includes(id) ? f.ward_ids.filter((x) => x !== id) : [...f.ward_ids, id],
  }));

  const reset = () => {
    setForm({ id: null, name: "", district_id: "", lsgd_id: "", ward_ids: [] });
    setLsgdSearch("");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    if (!form.lsgd_id) { toast.error("Select LSGD"); return; }
    try {
      const payload = {
        name: form.name, district_id: form.district_id || null,
        lsgd_id: form.lsgd_id || null, ward_ids: form.ward_ids,
      };
      if (form.id) {
        await api.put(`/zones/${form.id}`, payload);
        toast.success("Zone updated");
      } else {
        await api.post("/zones", payload);
        toast.success("Zone created");
      }
      reset();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this zone?")) return;
    try { await api.delete(`/zones/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const edit = (z) => {
    setForm({
      id: z.id, name: z.name,
      district_id: z.district_id || "",
      lsgd_id: z.lsgd_id || "",
      ward_ids: z.ward_ids || [],
    });
    setLsgdSearch("");
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("rescigo_token");
    const r = await fetch(`${API_BASE}/zones/template`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Download failed"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "zones_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportZones = async (kind) => {
    const token = localStorage.getItem("rescigo_token");
    const ext = kind === "pdf" ? "pdf" : "csv";
    try {
      const r = await fetch(`${API_BASE}/zones/export.${ext}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error("Export failed"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `health_zones_${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadCsv = async () => {
    if (!csvFile) { toast.error("Pick a CSV file"); return; }
    setCsvBusy(true); setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const r = await api.post("/zones/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(r.data);
      toast.success(`Imported · ${r.data.created} new · ${r.data.updated || 0} updated · ${r.data.errors?.length || 0} errors`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setCsvBusy(false);
  };

  // filter + sort zones for list
  const filtered = useMemo(() => {
    let rows = zones;
    if (listFilter.district_id) rows = rows.filter((z) => z.district_id === listFilter.district_id);
    if (listFilter.lsgd_id) rows = rows.filter((z) => z.lsgd_id === listFilter.lsgd_id);
    if (listFilter.q) rows = rows.filter((z) => z.name.toLowerCase().includes(listFilter.q.toLowerCase()));
    const dir = sort.dir === "asc" ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      if (sort.col === "name") return a.name.localeCompare(b.name) * dir;
      if (sort.col === "wards") return ((a.ward_ids?.length || 0) - (b.ward_ids?.length || 0)) * dir;
      if (sort.col === "lsgd") return ((lsgdName(a.lsgd_id) || "").localeCompare(lsgdName(b.lsgd_id) || "")) * dir;
      return 0;
    });
    return rows;
  }, [zones, listFilter, sort]); // eslint-disable-line

  const setSortCol = (col) => setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <AppShell title="Health Zones" subtitle="Master Control"
      actions={
        <div className="flex items-center gap-2">
          <button data-testid="btn-export-zones-csv" onClick={() => exportZones("csv")}
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <FileCsv size={12} weight="bold" />Export CSV
          </button>
          <button data-testid="btn-export-zones-pdf" onClick={() => exportZones("pdf")}
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <Download size={12} weight="bold" />Export PDF
          </button>
          <button data-testid="btn-import-zones" onClick={() => { setCsvOpen(true); setCsvFile(null); setCsvResult(null); }}
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <UploadSimple size={12} weight="bold" />CSV import
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-6 py-4 border-b border-[#E5E2D9] flex flex-wrap items-center gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Operational zones</div>
              <div className="font-heading text-lg">Grouped wards</div>
            </div>
            <div className="flex-1" />
            <select value={listFilter.district_id}
                    onChange={(e) => setListFilter((f) => ({ ...f, district_id: e.target.value, lsgd_id: "" }))}
                    data-testid="filter-district"
                    className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white">
              <option value="">All districts</option>
              {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select value={listFilter.lsgd_id}
                    onChange={(e) => setListFilter((f) => ({ ...f, lsgd_id: e.target.value }))}
                    data-testid="filter-lsgd"
                    className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white">
              <option value="">All LSGDs</option>
              {lsgds.filter((l) => !listFilter.district_id || l.parent_id === listFilter.district_id)
                    .map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8A8780]" />
              <input data-testid="filter-search" value={listFilter.q}
                     onChange={(e) => setListFilter((f) => ({ ...f, q: e.target.value }))}
                     placeholder="Search zone…"
                     className="h-9 pl-7 pr-2 border border-[#E5E2D9] rounded-md text-xs w-40" />
            </div>
          </div>

          <table className="w-full text-sm" data-testid="zones-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <Th col="name" sort={sort} onClick={setSortCol}>Zone</Th>
                <Th col="lsgd" sort={sort} onClick={setSortCol}>LSGD · District</Th>
                <Th col="wards" sort={sort} onClick={setSortCol}>Wards</Th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-12 text-center text-[#8A8780]">No zones match.</td></tr>
              )}
              {filtered.map((z, i) => (
                <tr key={z.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <Path size={14} color="#2D5A27" weight="fill" />
                      <span className="font-medium">{z.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-[#5C5A55] text-xs">
                    {lsgdName(z.lsgd_id) || "—"}{z.district_id ? ` · ${districtName(z.district_id) || ""}` : ""}
                  </td>
                  <td className="px-6 py-3 text-[#5C5A55]">{(z.ward_ids || []).length}</td>
                  <td className="px-6 py-3 text-right">
                    <button data-testid={`zone-edit-${z.id}`} onClick={() => edit(z)} className="text-[#2D5A27] text-xs inline-flex items-center gap-1 mr-4"><PencilSimple size={12} />Edit</button>
                    <button data-testid={`zone-del-${z.id}`} onClick={() => del(z.id)} className="text-[#C25D38] text-xs inline-flex items-center gap-1"><Trash size={12} />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Form */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">{form.id ? "Edit zone" : "New zone"}</div>
          <div className="font-heading text-lg mb-4">Group wards</div>
          <form onSubmit={save} className="space-y-4" data-testid="zone-form">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">District *</label>
              <select data-testid="zf-district" value={form.district_id}
                      onChange={(e) => setForm((f) => ({ ...f, district_id: e.target.value, lsgd_id: "", ward_ids: [] }))}
                      className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                <option value="">— select district —</option>
                {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">LSGD *</label>
              <div className="relative mb-2">
                <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8780]" />
                <input value={lsgdSearch} onChange={(e) => setLsgdSearch(e.target.value)}
                       placeholder="Search LSGD by name…" data-testid="zf-lsgd-search"
                       className="w-full h-9 pl-7 pr-2 border border-[#E5E2D9] rounded-md text-xs" />
              </div>
              <div className="max-h-40 overflow-y-auto border border-[#E5E2D9] rounded-md divide-y divide-[#E5E2D9]" data-testid="zf-lsgd-list">
                {districtLsgds.length === 0 && (
                  <div className="p-3 text-xs text-[#8A8780]">
                    {form.district_id ? "No LSGD in this district." : "Select a district to see LSGDs."}
                  </div>
                )}
                {districtLsgds.map((l) => (
                  <label key={l.id} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${form.lsgd_id === l.id ? "bg-[#D4E0D1]" : "hover:bg-[#F7F6F2]"}`}>
                    <input type="radio" name="lsgd" checked={form.lsgd_id === l.id}
                           onChange={() => setForm((f) => ({ ...f, lsgd_id: l.id, ward_ids: [] }))}
                           className="accent-[#2D5A27]" data-testid={`zf-lsgd-${l.id}`} />
                    <div className="flex-1">
                      <div className="font-medium text-sm leading-tight">{l.name}</div>
                      {l.lsgd_type && <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] capitalize">{l.lsgd_type.replace("_", " ")}</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Name *</label>
              <input data-testid="zone-name" required value={form.name}
                     onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Wards in this LSGD ({form.ward_ids.length})</label>
              <div className="max-h-48 overflow-y-auto border border-[#E5E2D9] rounded-md divide-y divide-[#E5E2D9]" data-testid="zone-ward-picker">
                {lsgdWards.length === 0 && (
                  <div className="p-3 text-xs text-[#8A8780]">
                    {form.lsgd_id ? "No wards under this LSGD." : "Select an LSGD to see wards."}
                  </div>
                )}
                {lsgdWards.map((w) => {
                  const taken = takenWards.has(w.id);
                  const checked = form.ward_ids.includes(w.id);
                  return (
                    <label key={w.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${taken ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-[#F7F6F2]"}`}>
                      <input type="checkbox" disabled={taken} checked={checked}
                             onChange={() => toggleWard(w.id)}
                             data-testid={`ward-${w.id}`}
                             className="accent-[#2D5A27]" />
                      <span className="flex-1">{w.ward_no && <span className="font-mono text-xs text-[#8A8780]">#{w.ward_no} </span>}{w.name}</span>
                      {taken && <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">In zone</span>}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2">
              <button data-testid="zone-save" type="submit"
                      className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                {form.id ? <><CheckCircle size={14} weight="bold" />Update</> : <><Plus size={14} weight="bold" />Create</>}
              </button>
              {form.id && <button type="button" onClick={reset} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>}
            </div>
          </form>

          <div className="mt-6 pt-5 border-t border-[#E5E2D9] flex items-center gap-2">
            <button onClick={downloadTemplate} data-testid="zone-tpl" className="h-8 px-2.5 text-xs border border-[#E5E2D9] rounded text-[#2D5A27] inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
              <Download size={11} />Template
            </button>
            <button onClick={() => { setCsvOpen(true); setCsvFile(null); setCsvResult(null); }} className="h-8 px-2.5 text-xs border border-[#E5E2D9] rounded inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
              <UploadSimple size={11} />CSV import
            </button>
          </div>
        </div>
      </div>

      {csvOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCsvOpen(false)}>
          <div className="bg-white w-full max-w-md rounded-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <FileCsv size={20} color="#2D5A27" weight="fill" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">CSV import</div>
                <div className="font-heading text-lg">Zones</div>
              </div>
            </div>
            <div className="text-xs text-[#5C5A55] bg-[#F7F6F2] border border-[#E5E2D9] rounded p-3 mb-4">
              Columns: <code>zone_name, state_name, district_name, lsgd_name, lsgd_type, ward_names</code>. Use <code>|</code> to separate multiple wards. Missing state/district/LSGD/wards are auto-created; existing zones are updated (ward links replaced).
            </div>
            <input type="file" accept=".csv,text/csv" data-testid="csv-zone-file"
                   onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                   className="w-full text-sm mb-4" />
            {csvResult && (
              <div className="text-xs bg-[#D4E0D1]/40 border border-[#D4E0D1] rounded p-3 mb-4">
                Created: <b>{csvResult.created}</b> · Updated: <b>{csvResult.updated || 0}</b> · Errors: <b>{csvResult.errors?.length || 0}</b>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setCsvOpen(false)} className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-sm">Close</button>
              <button onClick={uploadCsv} disabled={csvBusy || !csvFile} data-testid="csv-zone-upload"
                      className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <UploadSimple size={12} weight="bold" />{csvBusy ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const Th = ({ col, sort, onClick, children }) => (
  <th className="px-6 py-3 cursor-pointer select-none" onClick={() => onClick(col)} data-testid={`sort-${col}`}>
    <span className="inline-flex items-center gap-1">
      {children}
      {sort.col === col && (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
    </span>
  </th>
);
